/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import {getOmegaEvent} from '@exc/events';
import {
  getPathWithoutTenant,
  getQueryValue,
  hashToPath,
  updatePath
} from '@exc/url';
import {getShellComponents} from './imports/ImportManager';
import {history} from '@exc/router';
import {I18nShell} from '@exc/shell';
import metrics from '@adobe/exc-app/metrics';
import PropTypes from 'prop-types';
import React from 'react';

export function getSelectedWorkspace(ws) {
  let {hash, pathname, search} = history.location;
  const {origin} = window.location;
  pathname = getPathWithoutTenant(pathname);
  const {searchParams} = new URL(`${origin}${search}`);
  let extendedPath = `${pathname}${search}${hash}`;

  return getWorkspaces(ws).find(w => {
    if (/^http/.test(w.url)) {
      return false;
    }
    const selected = w.selectOn && w.selectOn.find(s => s.test(extendedPath));
    if (selected) {
      return selected;
    }
    let url = w.url.endsWith('/') ? w.url : `${w.url}/`;
    url = new URL(`${origin}${w.url}`);
    const {hash: wsHash, pathname: wsPath, searchParams: wsSearchParams} = url;
    if (!(new RegExp(`^${wsPath}?`).test(pathname))) {
      return false;
    }
    const params = Array.from(wsSearchParams.keys());
    return hash === wsHash && params.every(key => searchParams.get(key) === wsSearchParams.get(key));
  });
}

function getSelectedSideNavItemId(navMenu) {
  let pathname = getPathWithoutTenant(hashToPath());
  if (!pathname.endsWith('/')) {
    pathname = `${pathname}/`;
  }
  const itemMatch = url => new RegExp(`^${url}?`).test(pathname);

  for (let item of navMenu) {
    if (itemMatch(item.url)) {
      return item.id;
    } else if (item.items) { // for items nested in a header
      for (let nestedItem of item.items) {
        if (itemMatch(nestedItem.url)) {
          return nestedItem.id;
        }
      }
    }
  }
  return {};
}

/**
 * This function generates a list of workspaces, using BFS to identify menu
 * workspaces.
 * @param {Object[]} workspaces Array of workspaces.
 * @returns {Object[]} Array of workspaces including menu workspaces.
 */
function getWorkspaces(workspaces) {
  let queue = [];
  let menus = [];
  workspaces.forEach(w => {
    queue.push(w);
    while (queue.length) {
      let ws = queue.shift();
      menus.push(ws);
      ws.menu && ws.menu.forEach(m => queue.push(m));
    }
  });

  menus.sort((a, b) => a.url.split('/').length - b.url.split('/').length);
  menus.reverse();
  return menus;
}

export default class ShellWrapper extends React.Component {
  constructor(props) {
    super(props);
    this.metrics = metrics.create('exc.core.Shell');
    this.deferredComponentsPromise = getShellComponents();
  }

  onHeroClick(e) {
    if (e.type === 'click' || e.key === 'Enter') {
      const {basePath, hero, noSolution} = this.props;

      if (noSolution) {
        updatePath({path: '/home'});
        return;
      }

      this.metrics.analytics.trackEvent(getOmegaEvent({
        attributes: {icon: hero.iconName, link: this.props.customHeroClick ? 'custom' : basePath},
        element: e.target && e.target.innerText,
        event: e,
        widget: {name: 'hero', type: 'button'}
      }));

      if (this.props.customHeroClick) {
        this.props.customHeroClick();
        return;
      }
      updatePath({path: hero.path || basePath});
    }
  }

  onViewAllNotificationsClickHandler(activeTab = '') {
    updatePath({path: `/home/notifications/${activeTab}`});
  }

  render() {
    const props = this.props;
    const {
      digitalData,
      hero,
      locale,
      noAccess,
      pulse,
      selectedTenantId,
      shellResponse,
      sideNavConfig
    } = props;
    const customButtons = props.customButtons || {};
    const profile = props.profile || {};
    const router = React.Children.only(props.children);
    const selectedWorkspace = getSelectedWorkspace(props.workspaces);
    const selectedSideNavItemId = getSelectedSideNavItemId((sideNavConfig || {}).menu || []);

    const helpCenterConfig = {
      canAuthorize: props.environment === 'prod',
      customButtons,
      digitalData,
      environment: props.environment,
      feedbackAttachmentsEnabled: true,
      imsPayload: {
        accessToken: props.accessToken,
        displayName: profile.displayName,
        email: profile.email,
        firstName: profile.first_name,
        lastName: profile.last_name,
        userId: profile.userId
      },
      onCustomButtonClick: props.onCustomButtonClick,
      ...props.helpCenter,
      solutions: shellResponse.solutions,
      solutionTitle: hero.title
    };
    const pulseConfig = {
      ...pulse,
      onViewAllNotificationsClickHandler: this.onViewAllNotificationsClickHandler.bind(this)
    };

    return (
      <I18nShell
        appContainer={props.appContainer}
        appId={props.appId}
        clientId={props.clientId}
        coachMark={props.coachMark}
        currentActiveTenantId={selectedTenantId}
        customButtons={customButtons}
        customEnvLabel={props.customEnvLabel}
        customProfileButtons={customButtons.userProfile}
        customSearch={props.customSearch}
        defaultComponent={props.defaultComponent}
        deferredComponentsPromise={this.deferredComponentsPromise}
        environment={props.environment}
        feedback={props.feedback}
        getQueryValue={getQueryValue}
        globalSearch={props.globalSearch}
        hasAccountClusterData={props.hasAccountClusterData}
        helpCenterConfig={helpCenterConfig}
        imsOrgs={props.imsOrgs}
        landingpage={shellResponse.landingpage}
        locale={locale}
        modal={props.modal}
        noAccess={noAccess}
        nps={props.nps}
        onCustomButtonClick={props.onCustomButtonClick}
        onCustomProfileButtonClick={props.onCustomButtonClick}
        onImsOrgChange={props.onImsOrgChange}
        onSignOutClick={props.onSignOut}
        onSolutionClick={this.onHeroClick.bind(this)}
        orgToSubOrgsMap={props.orgToSubOrgsMap}
        profile={profile}
        pulse={pulseConfig}
        releaseType={props.releaseType}
        sandbox={props.sandbox}
        selectedImsOrg={props.selectedImsOrg}
        selectedSideNavItemId={selectedSideNavItemId}
        selectedSubOrg={props.selectedSubOrg}
        selectedWorkspaceId={selectedWorkspace && selectedWorkspace.uid}
        serviceEnvironment={props.serviceEnvironment}
        services={shellResponse.services}
        settingsService={props.settingsService}
        sideNavConfig={props.sideNavConfig}
        sideNavEnabled={props.sideNavEnabled}
        sideNavOpen={props.sideNavOpen}
        solutionIcon={hero.iconName}
        solutionShortTitle={hero.shortTitle}
        solutionTitle={hero.title}
        solutions={shellResponse.solutions}
        supportsViewportTakeover={props.supportsViewportTakeover}
        toggleSideNav={props.toggleSideNav}
        translationService={props.translationService}
        userAvatar={props.userAvatar}
        userToken={props.accessToken}
        viewportTakeover={props.viewportTakeover}
        workspaces={props.workspaces}
      >
        <React.Suspense fallback={props.defaultComponent}>{router}</React.Suspense>
      </I18nShell>
    );
  }
}

ShellWrapper.propTypes = {
  accessToken: PropTypes.string,
  appContainer: PropTypes.string,
  basePath: PropTypes.string,
  children: PropTypes.element.isRequired,
  customEnvLabel: PropTypes.string,
  customSearch: PropTypes.object,
  defaultComponent: PropTypes.object,
  environment: PropTypes.string,
  globalSearch: PropTypes.shape({
    accessToken: PropTypes.string,
    enabled: PropTypes.bool,
    selectedImsOrg: PropTypes.string
  }),
  hasAccountClusterData: PropTypes.bool,
  helpCenter: PropTypes.object,
  hero: PropTypes.object,
  imsOrgs: PropTypes.array,
  locale: PropTypes.string,
  noAccess: PropTypes.bool,
  nps: PropTypes.object,
  onImsOrgChange: PropTypes.func,
  onSignOut: PropTypes.func,
  profile: PropTypes.object,
  releaseType: PropTypes.string,
  sandbox: PropTypes.shape({
    enabled: PropTypes.bool,
    onChange: PropTypes.func,
    sandboxes: PropTypes.array,
    selected: PropTypes.object
  }),
  selectedImsOrg: PropTypes.string,
  serviceEnvironment: PropTypes.string,
  shellResponse: PropTypes.object,
  sideNavConfig: PropTypes.object,
  sideNavEnabled: PropTypes.bool,
  toggleSideNav: PropTypes.func,
  userAvatar: PropTypes.string,
  workspaces: PropTypes.array
};

ShellWrapper.defaultProps = {
  accessToken: null,
  appContainer: null,
  customEnvLabel: '',
  customSearch: {
    enabled: false,
    onClick: () => {},
    open: false
  },
  environment: 'dev',
  hasAccountClusterData: false,
  helpCenter: {
    featured: [],
    resources: []
  },
  hero: {
    icon: null,
    shortTitle: '',
    title: ''
  },
  imsOrgs: [],
  locale: 'en-US',
  noAccess: false,
  nps: {
    enabled: false,
    sampling: 10,
    showToAdobeUsers: true
  },
  profile: {},
  releaseType: '',
  sandbox: {
    enabled: false,
    onChange: () => {},
    sandboxes: [],
    selected: null
  },
  selectedImsOrg: null,
  sideNavConfig: {},
  sideNavEnabled: false,
  toggleSideNav: () => {},
  workspaces: []
};
